import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { Check, X, Info } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    // Dedup by (message, type): si el mismo toast ya está visible, no apilar.
    // (N pollers de 401 empujan "Session expired" a la vez → no spamear.)
    const id = ++idRef.current;
    setToasts((prev) =>
      prev.some((t) => t.message === message && t.type === type) ? prev : [...prev, { id, message, type }],
    );
    // Siempre agendamos el auto-remove para ESTE id. Si fue deduplicado, el filtro
    // no encuentra el id y es no-op (el toast existente tiene su propio timer).
    // OJO: no condicionar al resultado del updater — corre async y dejaba toasts
    // sin timer ("Copiado" que no se cerraba).
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              px-4 py-3 rounded-lg border shadow-lg
              animate-slide-in
              flex items-start gap-3
              ${toast.type === 'success'
                ? 'bg-[var(--success)]/10 border-[var(--success)]/30 text-[var(--success-fg)]'
                : toast.type === 'error'
                ? 'bg-[var(--danger)]/10 border-[var(--danger)]/30 text-[var(--danger-fg)]'
                : 'bg-[var(--info)]/10 border-[var(--info)]/30 text-[var(--info-fg)]'
              }
            `}
          >
            <span className="text-lg">
              {toast.type === 'success' ? <Check size={16} /> : toast.type === 'error' ? <X size={16} /> : <Info size={16} />}
            </span>
            <p className="flex-1 text-sm">{toast.message}</p>
            <button
              onClick={() => removeToast(toast.id)}
              className="text-[var(--text-muted)] hover:text-[var(--text-heading)] transition"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
